import { join } from 'node:path';
import z from 'zod';
import { GIT_IGNORED_DIR, getGitIgnoredPath } from '@wix/cli-core-definitions';

const wixAstroSiteConfigSchema = z.object({
  $schema: z.string().optional(),
  projectType: z.literal('Site').optional().default('Site'),
  appId: z.string(),
  siteId: z.string(),
});

const wixAstroAppConfigSchema = z.object({
  $schema: z.string().optional(),
  projectType: z.literal('App'),
  appId: z.string(),
  projectId: z.string().optional(),
});

export const wixAstroConfigSchema = z.discriminatedUnion('projectType', [
  wixAstroSiteConfigSchema,
  wixAstroAppConfigSchema,
]);

export type WixAstroConfig = z.infer<typeof wixAstroConfigSchema>;

export const ENV_FILE_NAME = '.env.local';
export const TRANSLATIONS_FILE_PATH = join('src', 'translations.json');
export const BACKEND_WORKER_COMPONENT_ID =
  'ed5f3d0e-7b79-4717-9c00-c4cd7bbbe906';

export const devMetadataSchema = z.object({
  staticsUrlPlaceholder: z.string(),
  serverUrlPlaceholder: z.string(),
});

export type DevMetadata = z.infer<typeof devMetadataSchema>;

export const buildMetadataSchema = z.object({
  appManifestPath: z.string(),
  outDir: z.string(),
  clientDir: z.string(),
  serverDir: z.string().optional(),
  staticsUrlPlaceholder: z.string().optional(),
  serverUrlPlaceholder: z.string().optional(),
});

const deploymentTopologyEnvironmentSchema = z.object({
  id: z.string().describe('Unique ARM tag identifier'),
  site: z
    .object({
      main: z.string().url().describe('Main site URL'),
    })
    .describe('Site topology details')
    .optional(),
  dashboard: z
    .object({
      main: z
        .string()
        .url()
        .describe('Main dashboard URL (link to a business manager dashboard)'),
    })
    .describe('Optional dashboard topology details')
    .optional(),
  editor: z
    .object({
      main: z.string().url().describe('Main editor URL'),
    })
    .describe('Editor topology details')
    .optional(),
});

export type DeploymentTopologyEnvironmentSchema = z.infer<
  typeof deploymentTopologyEnvironmentSchema
>;

const deploymentTopologyEnvironmentTypeSchema = z.enum([
  'production',
  'preview',
  'development',
]);

export type DeploymentTopologyEnvironmentType = z.infer<
  typeof deploymentTopologyEnvironmentTypeSchema
>;

export const deploymentTopologySchema = z.record(
  deploymentTopologyEnvironmentTypeSchema,
  deploymentTopologyEnvironmentSchema
);

export function getBuildManifestPath(projectFolder: string) {
  return getGitIgnoredPath(projectFolder, 'build-metadata.json');
}

export function getDeploymentTopologyPath(projectFolder: string) {
  return getGitIgnoredPath(projectFolder, 'topology.json');
}

export function getEnvFilePath(projectFolder: string) {
  return join(projectFolder, ENV_FILE_NAME);
}

export function getExtensionDir(projectFolder: string) {
  return join(projectFolder, 'src', 'extensions');
}

const MULTILINGUAL_DIR = join(GIT_IGNORED_DIR, 'multilingual');
export const TRANSLATIONS_DIR = join(MULTILINGUAL_DIR, 'translations');
export const MULTILINGUAL_METADATA_FILENAME = 'metadata.json';

export function getTranslationsDir(projectFolder: string) {
  return join(projectFolder, TRANSLATIONS_DIR);
}

export function getMultilingualMetadataPath(projectFolder: string) {
  return join(projectFolder, MULTILINGUAL_DIR, MULTILINGUAL_METADATA_FILENAME);
}

export function getDebugLogPath(projectFolder: string) {
  return getGitIgnoredPath(projectFolder, 'debug-logs', 'cli-debug.log');
}
